import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logoImg from '../logo.jpg';

export const TermsOfServicePage: React.FC = () => {
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const sections = [
        {
            title: "Acceptance of Terms",
            paragraphs: [
                "By accessing or using Tvora: Analytics & Profit, you confirm that you have the authority to use the Shopify store connected to the application and agree to follow these Terms of Service.",
                "If you do not agree with any part of these terms, you should discontinue the use of the application and related services."
            ]
        },
        {
            title: "Description of Service",
            paragraphs: [
                "Tvora: Analytics & Profit is a Shopify-integrated analytics and business intelligence platform designed to help merchants understand store performance through data-driven insights.",
                "The platform may include features such as:",
                "• Performance analytics\n• Order intelligence\n• Customer segmentation\n• Product analytics\n• Inventory insights\n• Profit tracking\n• Reporting and exports\n• Forecasting and business intelligence\n• Dashboard customization\n• Store data synchronization",
                "We continuously improve and update the platform to provide better functionality and user experience."
            ]
        },
        {
            title: "User Responsibilities",
            paragraphs: [
                "Users are responsible for maintaining accurate account information and ensuring that their Shopify store access credentials remain secure.",
                "When using Tvora, you agree not to:",
                "• Use the platform for unlawful activities\n• Attempt unauthorized access to systems or data\n• Disrupt or interfere with platform performance\n• Misuse exports, analytics, or reporting tools\n• Upload harmful software, malicious code, or automated abuse scripts\n• Attempt to reverse engineer or copy proprietary platform functionality",
                "You are responsible for ensuring that your use of the application complies with applicable laws and Shopify platform policies."
            ]
        },
        {
            title: "Shopify Account Integration",
            paragraphs: [
                "Tvora operates through Shopify APIs and requires authorized permissions to access relevant store data. By installing the application, you grant permission for the platform to process the information required to provide analytics and intelligence features.",
                "Permissions may include access to:",
                "• Orders\n• Products\n• Customers\n• Inventory\n• Store analytics\n• Fulfillment data\n• Related operational information",
                "The level of access depends on the services and features being used within the application."
            ]
        },
        {
            title: "Data Accuracy",
            paragraphs: [
                "Tvora aims to provide accurate analytics and reporting based on the available store data. However, the platform cannot guarantee that all metrics, calculations, forecasts, or reports will always be completely error-free or uninterrupted.",
                "Analytics may occasionally be affected by:",
                "• Delayed store synchronization\n• Shopify API limitations\n• Third-party integrations\n• Data inconsistencies\n• Temporary technical interruptions",
                "Users are encouraged to independently verify critical business decisions before relying solely on platform-generated insights."
            ]
        },
        {
            title: "Subscription and Billing",
            paragraphs: [
                "Some features of Tvora may require a paid subscription or premium access plan. Pricing, billing cycles, and feature availability may vary depending on the selected plan.",
                "By subscribing to a paid plan, you authorize applicable billing through Shopify or related payment systems.",
                "Users are responsible for:",
                "• Maintaining valid payment methods\n• Reviewing billing details\n• Managing plan upgrades or cancellations",
                "Failure to maintain active billing may result in restricted access to certain features or services."
            ]
        },
        {
            title: "Cancellation and Uninstallation",
            paragraphs: [
                "Users may uninstall the application or cancel their subscription at any time through Shopify or the available account management settings.",
                "After uninstallation:",
                "• Future store synchronization will stop\n• Access to dashboards and reports may be disabled\n• Certain stored information may remain temporarily for backup, operational, legal, or security purposes",
                "Removing the application does not automatically erase all historical data immediately."
            ]
        },
        {
            title: "Intellectual Property",
            paragraphs: [
                "All content, software, branding, platform design, analytics systems, visual elements, reports, and proprietary technology associated with Tvora: Analytics & Profit remain the intellectual property of Tvora and its operators.",
                "Users may not:",
                "• Reproduce platform functionality\n• Copy or distribute proprietary components\n• Resell or commercially exploit the platform without authorization\n• Use Tvora branding without permission",
                "The platform is provided for legitimate business usage within the scope of these terms."
            ]
        },
        {
            title: "Platform Availability",
            paragraphs: [
                "We work continuously to maintain platform reliability and performance. However, we cannot guarantee uninterrupted access at all times.",
                "Temporary downtime may occur due to:",
                "• Maintenance updates\n• Shopify API issues\n• Infrastructure upgrades\n• Security improvements\n• Unexpected technical problems",
                "We reserve the right to modify, suspend, or discontinue specific features when necessary to improve the platform or maintain operational stability."
            ]
        },
        {
            title: "Limitation of Liability",
            paragraphs: [
                "Tvora: Analytics & Profit is provided on an “as available” basis. While we strive to deliver reliable analytics and business intelligence tools, we are not responsible for indirect business losses resulting from the use or inability to use the platform.",
                "This includes but is not limited to:",
                "• Loss of revenue\n• Business interruption\n• Data inaccuracies\n• Reporting delays\n• Forecasting errors\n• Store performance changes\n• Third-party platform issues",
                "Users accept that analytics tools are designed to support decision-making but do not guarantee specific business outcomes or financial results."
            ]
        },
        {
            title: "Privacy and Data Handling",
            paragraphs: [
                "Your use of Tvora is also subject to our Privacy Policy, which explains how information is collected, processed, stored, and protected.",
                "We are committed to maintaining responsible data handling practices and protecting merchant information using reasonable security measures."
            ]
        },
        {
            title: "Third-Party Services",
            paragraphs: [
                "Tvora may integrate with third-party tools, infrastructure providers, or external services to support analytics, hosting, exports, or operational features.",
                "We are not responsible for interruptions, policies, or practices associated with third-party services outside our direct control.",
                "Users should review the applicable policies of external platforms where relevant."
            ]
        },
        {
            title: "Changes to Terms",
            paragraphs: [
                "We may update these Terms of Service periodically to reflect operational changes, legal requirements, platform improvements, or new features.",
                "Updated versions may be published within the application or official website. Continued use of Tvora after changes are published indicates acceptance of the updated terms.",
                "We encourage users to review these terms regularly."
            ]
        },
        {
            title: "Termination",
            paragraphs: [
                "We reserve the right to suspend or terminate access to the platform if a user violates these Terms of Service, misuses the application, attempts unauthorized activity, or creates security or operational risks for the platform or other users.",
                "Termination may occur without prior notice in serious cases involving abuse, fraud, or harmful activity."
            ]
        },
        {
            title: "Contact and Support",
            paragraphs: [
                "If you have questions regarding these Terms of Service, billing, account access, platform usage, or technical concerns, you may contact the Tvora support team through the official support channels available within the application or website.",
                "At Tvora: Analytics & Profit, our mission is to provide Shopify merchants with a reliable and intelligent analytics platform that supports smarter growth, operational visibility, and data-driven decision-making. We appreciate your trust in our platform and remain committed to delivering a professional and secure experience for all users."
            ]
        }
    ];

    return (
        <div style={{
            minHeight: '100vh',
            fontFamily: "'Outfit', 'Inter', system-ui, -apple-system, sans-serif",
            color: '#0f172a',
            background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 40%, #f5f3ff 70%, #faf5ff 100%)',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <header className="landing-header" style={{
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                background: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderBottom: '1px solid rgba(241, 245, 249, 0.8)',
                padding: '1.25rem 2rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 4px 30px rgba(0, 0, 0, 0.01)'
            }}>
                <div 
                    className="header-logo" 
                    onClick={() => navigate('/')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', cursor: 'pointer' }}
                >
                    <img 
                        src={logoImg} 
                        alt="Tvora Logo" 
                        style={{
                            width: '38px',
                            height: '38px',
                            borderRadius: '10px',
                            objectFit: 'cover',
                            boxShadow: '0 4px 20px rgba(99, 102, 241, 0.15)'
                        }} 
                    />
                    <div>
                        <span className="logo-title" style={{
                            fontWeight: 800,
                            fontSize: '1.45rem',
                            letterSpacing: '-0.03em',
                            background: 'linear-gradient(135deg, #1e293b 0%, #6366f1 100%)',
                            WebkitBackgroundClip: 'text',
                            backgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            Tvora
                        </span>
                        <span className="logo-subtitle" style={{
                            display: 'block',
                            fontSize: '0.65rem',
                            color: '#64748b',
                            fontWeight: 600,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            marginTop: '-2px'
                        }}>
                            Analytics & Profit
                        </span>
                    </div>
                </div>

                <nav style={{ display: 'flex', gap: '2rem', alignItems: 'center' }} className="nav-desktop">
                    <a href="/#features" onClick={() => navigate('/#features')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Features</a>
                    <a href="/#showcase" onClick={() => navigate('/#showcase')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Interactive Preview</a>
                    <a href="/#who-is-it-for" onClick={() => navigate('/#who-is-it-for')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Target Audience</a>
                    <a href="/#pricing" onClick={() => navigate('/#pricing')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Pricing</a>
                    <a href="/#faq" onClick={() => navigate('/#faq')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>FAQ</a>
                </nav>

                <div className="header-actions" style={{ display: 'flex', gap: '0.85rem', alignItems: 'center' }}>
                    <a href="/" onClick={() => navigate('/')} style={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
                        color: 'white',
                        padding: '0.65rem 1.35rem',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        boxShadow: '0 8px 25px rgba(99, 102, 241, 0.25)',
                        transition: 'all 0.2s'
                    }}>
                        Back to Home
                    </a>
                </div>

                {/* Hamburger Toggle Button */}
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="hamburger-button"
                    style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        color: '#0f172a',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {isMobileMenuOpen ? (
                        <svg style={{ width: '26px', height: '26px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    ) : (
                        <svg style={{ width: '26px', height: '26px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    )}
                </button>
            </header>

            {/* Mobile Navigation Drawer */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        style={{
                            background: 'rgba(255, 255, 255, 0.98)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                            position: 'fixed',
                            top: '64px',
                            left: 0,
                            right: 0,
                            zIndex: 999,
                            overflow: 'hidden',
                            boxShadow: '0 15px 30px rgba(0, 0, 0, 0.05)'
                        }}
                        className="mobile-menu-drawer"
                    >
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '1.5rem',
                            gap: '1.25rem'
                        }}>
                            <a href="/" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Home</a>
                            <a href="/#features" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#features'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Features</a>
                            <a href="/#showcase" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#showcase'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Interactive Preview</a>
                            <a href="/#who-is-it-for" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#who-is-it-for'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Target Audience</a>
                            <a href="/#pricing" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#pricing'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Pricing</a>
                            <a href="/#faq" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#faq'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>FAQ</a>
                            <hr style={{ border: 'none', borderTop: '1px solid #f1f5f9', margin: '0.25rem 0' }} />
                            <a href="/support" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/support'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Support Contact</a>
                            <a href="/privacy" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/privacy'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Privacy Policy</a>
                            <a href="/terms" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/terms'); }} style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Terms of Service</a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Content */}
            <main style={{ flex: 1, padding: '4rem 2rem', maxWidth: '1000px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                {/* Title Section */}
                <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
                    <span style={{
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        color: '#6366f1',
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        display: 'block',
                        marginBottom: '0.75rem'
                    }}>
                        Legal Agreements
                    </span>
                    <h1 style={{
                        fontSize: '2.8rem',
                        fontWeight: 800,
                        letterSpacing: '-0.03em',
                        color: '#0f172a',
                        margin: 0,
                        lineHeight: 1.15
                    }}>
                        Terms of Service
                    </h1>
                    <p style={{
                        fontSize: '1.2rem',
                        color: '#64748b',
                        marginTop: '0.8rem',
                        fontWeight: 500
                    }}>
                        Tvora: Analytics & Profit Platform Terms of Service
                    </p>
                </div>

                {/* Introductory Card */}
                <div className="glass-card" style={{
                    padding: '2.5rem',
                    background: 'rgba(255, 255, 255, 0.65)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    borderRadius: '24px',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.02)',
                    lineHeight: '1.7',
                    marginBottom: '2.5rem'
                }}>
                    <p style={{ margin: '0 0 1.25rem 0', fontSize: '1.05rem', color: '#334155' }}>
                        Welcome to <strong>Tvora: Analytics & Profit</strong>. These Terms of Service explain the rules, responsibilities, and conditions related to using our platform, website, services, and Shopify application. By installing, accessing, or using Tvora, you agree to comply with these terms.
                    </p>
                    <p style={{ margin: '0', fontSize: '1.05rem', color: '#334155' }}>
                        Our goal is to provide Shopify merchants with a reliable analytics and business intelligence platform that helps simplify decision-making through powerful reporting, customer insights, profit tracking, and operational analytics. We encourage all users to read these terms carefully before using the platform.
                    </p>
                </div>

                {/* Document Sections */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    {sections.map((section, idx) => (
                        <div key={idx} className="glass-card" style={{
                            padding: '2.25rem',
                            background: 'rgba(255, 255, 255, 0.65)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.6)',
                            borderRadius: '24px',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.02)'
                        }}>
                            <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0f172a', margin: '0 0 1.25rem 0' }}>
                                {section.title}
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', lineHeight: '1.65' }}>
                                {section.paragraphs.map((p, pIdx) => {
                                    if (p.includes('\n')) {
                                        return (
                                            <div key={pIdx} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', paddingLeft: '0.5rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                                                {p.split('\n').map((li, liIdx) => (
                                                    <div key={liIdx} style={{ display: 'flex', gap: '0.5rem', color: '#334155', fontSize: '1rem', fontWeight: 500 }}>
                                                        <span style={{ color: '#6366f1' }}>•</span>
                                                        <span>{li.replace('• ', '')}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    }
                                    return (
                                        <p key={pIdx} style={{ margin: 0, fontSize: '1rem', color: '#475569' }}>
                                            {p}
                                        </p>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </main>

            {/* Footer */}
            <footer style={{
                background: '#0f172a',
                color: '#94a3b8',
                padding: '4rem 2rem 2.5rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                marginTop: '4rem'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '3rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '3rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '320px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }} onClick={() => navigate('/')}>
                                <img src={logoImg} alt="Tvora" style={{ width: '32px', height: '32px', borderRadius: '8px' }} />
                                <span style={{ fontWeight: 800, fontSize: '1.25rem', color: 'white', letterSpacing: '-0.02em' }}>Tvora</span>
                            </div>
                            <p style={{ fontSize: '0.85rem', lineHeight: 1.6, margin: 0 }}>
                                Advanced business intelligence, real-time sync, customer retention modeling, and net profit calculations for growth-minded Shopify store owners.
                            </p>
                        </div>

                        <div style={{ display: 'flex', gap: '4rem', flexWrap: 'wrap' }}>
                            <div>
                                <h5 style={{ color: 'white', margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: 700 }}>Product</h5>
                                <ul style={{ padding: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem' }}>
                                    <li><a href="/#features" onClick={() => navigate('/#features')} style={{ color: '#64748b', textDecoration: 'none' }}>Features</a></li>
                                    <li><a href="/#showcase" onClick={() => navigate('/#showcase')} style={{ color: '#64748b', textDecoration: 'none' }}>Dashboard Showcase</a></li>
                                    <li><a href="/#pricing" onClick={() => navigate('/#pricing')} style={{ color: '#64748b', textDecoration: 'none' }}>Pricing Plans</a></li>
                                </ul>
                            </div>
                            <div>
                                <h5 style={{ color: 'white', margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: 700 }}>Resources</h5>
                                <ul style={{ padding: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem' }}>
                                    <li><a href="/#faq" onClick={() => navigate('/#faq')} style={{ color: '#64748b', textDecoration: 'none' }}>FAQ</a></li>
                                    <li><a href="/support" onClick={() => navigate('/support')} style={{ color: '#64748b', textDecoration: 'none' }}>Support Contact</a></li>
                                    <li><a href="/privacy" onClick={() => navigate('/privacy')} style={{ color: '#64748b', textDecoration: 'none' }}>Privacy Policy</a></li>
                                    <li><a href="/terms" onClick={() => navigate('/terms')} style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>Terms of Service</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '2rem', fontSize: '0.82rem' }}>
                        <span>© {new Date().getFullYear()} Tvora: Analytics & Profit. All rights reserved.</span>
                        <span>Need assistance? Contact <a href="mailto:support@tvora.co" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>support@tvora.co</a></span>
                    </div>
                </div>
            </footer>

            {/* Mobile/Desktop styling overrides */}
            <style dangerouslySetInnerHTML={{__html: `
                .hamburger-button {
                    display: none !important;
                }
                @media (max-width: 768px) {
                    main {
                        padding: 2.5rem 1.25rem !important;
                    }
                    h1 {
                        font-size: 2rem !important;
                    }
                    .glass-card {
                        padding: 1.5rem !important;
                    }
                    .nav-desktop, .header-actions {
                        display: none !important;
                    }
                    .landing-header .hamburger-button {
                        display: flex !important;
                        margin-left: auto !important;
                    }
                    .mobile-menu-drawer {
                        top: 64px !important;
                    }
                }
            `}} />
        </div>
    );
};
